import React, { useEffect, useMemo, ReactNode } from 'react'
import { Layout, Typography, Tooltip } from 'antd'
import styled from 'styled-components'
import { TopMenu } from './top-menu'
import { UserProfile } from './user-profile'
import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { getDataOfType } from 'src/lib/utils/get-data-of-type'
import { useHeaderActions } from '../../contexts/header-actions-context'
import { useMediaQuery } from 'react-responsive'
import { LeftOutlined } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { normalizePath } from '../../utilits/normalize-path'
import { NotificationBell } from './notifications/notification-bell'
import { useNavigationBlockerContext } from '../../contexts/navigation-blocker-context'

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

const AI_ROUTE_PREFIXES = ['/ai2', '/ai', '/mcp-ui']
const LAST_NON_AI_ROUTE_KEY = 'lastNonAiRoute'

const isPathInPrefix = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

const getAiRoutePrefix = (pathname: string) =>
  AI_ROUTE_PREFIXES.find(prefix => isPathInPrefix(pathname, prefix)) || null

const normalizeFallbackPath = (value: unknown) => {
  const path = String(value || '').trim()
  return path.startsWith('/') ? path : '/orders'
}

const getAiListPath = (prefix: string, section: 'chats' | 'reports') => {
  if (prefix === '/mcp-ui') {
    return `${prefix}/${section === 'chats' ? 'mcp-agents' : 'mcp-reports'}`
  }

  return `${prefix}/${section}`
}

const getAiReportPath = (prefix: string, reportId: string) =>
  `${getAiListPath(prefix, 'reports')}/${reportId}`

const getStoredNonAiRoute = () => {
  try {
    const raw = sessionStorage.getItem(LAST_NON_AI_ROUTE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const pathname = String(parsed?.pathname || '')
    if (!pathname || getAiRoutePrefix(pathname)) return null
    return {
      pathname,
      search: String(parsed?.search || ''),
      hash: String(parsed?.hash || ''),
    }
  } catch {
    return null
  }
}

const storeNonAiRoute = (pathname: string, search: string, hash: string) => {
  if (!pathname || getAiRoutePrefix(pathname)) return
  sessionStorage.setItem(LAST_NON_AI_ROUTE_KEY, JSON.stringify({ pathname, search, hash }))
}

const resolveAiBackTarget = (
  pathname: string,
  search: string,
  hash: string,
  fallbackPath: string,
) => {
  const prefix = getAiRoutePrefix(pathname)
  if (!prefix) return null

  const suffix = pathname.slice(prefix.length) || '/'
  const chatMatch = suffix.match(/^\/(?:chats|agents|mcp-agents)\/([^/]+)$/)
  const reportMatch = suffix.match(/^\/(?:reports|mcp-reports)\/([^/]+)$/)

  if (chatMatch) {
    const reportId = new URLSearchParams(search).get('reportId')
    return reportId
      ? { pathname: getAiReportPath(prefix, reportId), search: '', hash: '' }
      : { pathname: getAiListPath(prefix, 'chats'), search: '', hash: '' }
  }

  if (reportMatch) {
    return { pathname: getAiListPath(prefix, 'reports'), search: '', hash: '' }
  }

  if (
    suffix === '/' ||
    suffix === '/chats' ||
    suffix === '/reports' ||
    suffix === '/agents' ||
    suffix === '/mcp-agents' ||
    suffix === '/mcp-reports'
  ) {
    const storedRoute = getStoredNonAiRoute()
    return storedRoute || { pathname: fallbackPath, search: '', hash: '' }
  }

  return { pathname: getAiListPath(prefix, 'chats'), search: '', hash: '' }
}

interface IBreadcrumb {
  href?: string
  title: string
}

interface HeaderProps {
  pageTitle?: ReactNode
  backHref?: string
  breadcrumbs?: IBreadcrumb[]
}

const StyledHeader = styled(Layout.Header)`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 901;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  border-bottom: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    height: auto;
    min-height: 64px;
    flex-wrap: wrap;
    gap: 8px;
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const BackRow = styled.div``

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    flex: 1;
  }
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    max-width: 100%;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
`

const ActionButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  transition: color 0.3s;

  &:hover {
    color: #8b9ca7;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const PageTitle = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 20px !important;

  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`

const OfficeName = styled(Typography.Text)`
  font-size: 12px;
  color: #00000072;

  @media (max-width: 768px) {
    font-size: 10px;
  }
`

export const Header: React.FC<HeaderProps> = ({ pageTitle, breadcrumbs = [], backHref }) => {
  const { sessionData } = useSelector(createSession)
  const { headerActions } = useHeaderActions()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const navigate = useNavigate()
  const location = useLocation()
  const { runBlockers } = useNavigationBlockerContext()
  const fallbackPath = normalizeFallbackPath(
    getDataOfType(sessionData, 'start_page', String, '/orders'),
  )
  const aiBackTarget = useMemo(
    () => resolveAiBackTarget(location.pathname, location.search, location.hash, fallbackPath),
    [fallbackPath, location.hash, location.pathname, location.search],
  )

  const officeName = useMemo(() => {
    return getDataOfType(sessionData, 'user.office.name', String, '')
  }, [sessionData])

  useEffect(() => {
    if (aiBackTarget) return
    storeNonAiRoute(location.pathname, location.search, location.hash)
  }, [aiBackTarget, location.hash, location.pathname, location.search])

  const validBreadcrumbs = useMemo(() => breadcrumbs.filter(Boolean), [breadcrumbs])
  const showBackButton = Boolean(backHref) || validBreadcrumbs.length >= 2

  const handleBackClick = async () => {
    const allowed = await runBlockers()
    if (!allowed) {
      return
    }

    if (backHref) {
      navigate(backHref)
      return
    }

    if (aiBackTarget) {
      navigate(aiBackTarget)
      return
    }
    const fromState = (
      location.state as { from?: { pathname: string; search?: string } } | undefined
    )?.from
    if (fromState) {
      navigate({ pathname: fromState.pathname, search: fromState.search ?? '' })
      return
    }

    if (validBreadcrumbs.length >= 2) {
      const parentBreadcrumb = validBreadcrumbs[validBreadcrumbs.length - 2]
      if (parentBreadcrumb.href) {
        const normalizedHref = normalizePath(parentBreadcrumb.href)
        const storedSearch =
          sessionStorage.getItem(`listParams:${parentBreadcrumb.href}`) ??
          sessionStorage.getItem(`listParams:${normalizedHref}`) ??
          ''

        navigate({ pathname: parentBreadcrumb.href, search: storedSearch })
        return
      }
    }

    navigate(-1)
  }

  return (
    <StyledHeader>
      <LeftSection>
        {showBackButton && (
          <BackRow>
            <ActionButton
              as="button"
              type="button"
              aria-label="Назад"
              onClick={handleBackClick}
              style={{ background: 'transparent' }}
            >
              <LeftOutlined />
            </ActionButton>
          </BackRow>
        )}
        <HeaderInfo>
          {officeName && <OfficeName>{officeName}</OfficeName>}
          {pageTitle && <PageTitle level={3}>{pageTitle}</PageTitle>}
        </HeaderInfo>
      </LeftSection>
      <RightSection>
        {isMobile ? (
          <>
            <NotificationBell />
            {headerActions.map(action => (
              <Tooltip key={action.key} title={action.tooltip}>
                <ActionButton onClick={action.onClick}>{action.icon}</ActionButton>
              </Tooltip>
            ))}
            <UserProfile />
          </>
        ) : (
          <>
            <TopMenu />
            <NotificationBell />
            <UserProfile />
          </>
        )}
      </RightSection>
    </StyledHeader>
  )
}
